/**
 * AdminFounderPositionsCard — Super Admin control surface for the 100
 * canonical Founder positions. Renders a scrollable grid (#1 → #100),
 * exposes atomic Assign / Move / Reserve / Release with mandatory admin
 * reasons and confirmation warnings on any overwrite.
 *
 * The grid is the source-of-truth for a business's Founder number.
 * `founder_positions` collection on the backend enforces uniqueness and
 * writes to the shared founder_grants audit log.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Loader2, Search, Crown, Lock, Star, RotateCcw, X, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const STATUS_STYLES = {
  ASSIGNED: {
    dot: "bg-tbc-gold",
    ring: "border-tbc-gold/40 bg-tbc-navy/40",
    label: "text-tbc-gold",
    icon: Crown,
  },
  RESERVED: {
    dot: "bg-amber-500",
    ring: "border-amber-500/40 bg-amber-500/5",
    label: "text-amber-600",
    icon: Lock,
  },
  AVAILABLE: {
    dot: "bg-emerald-500",
    ring: "border-emerald-500/25 bg-white",
    label: "text-emerald-600",
    icon: Star,
  },
};

export default function AdminFounderPositionsCard() {
  const [grid, setGrid] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | assigned | available | reserved
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [actionOpen, setActionOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/founder/positions/admin/grid`, { withCredentials: true });
      setGrid(r.data.positions || []);
      setSummary(r.data.summary || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load positions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    if (filter === "all") return grid;
    const want = filter.toUpperCase();
    return grid.filter((p) => p.status === want);
  }, [grid, filter]);

  const handleOpen = (pos) => {
    setSelectedPosition(pos);
    setActionOpen(true);
  };

  return (
    <div className="rounded-2xl border border-tbc-navy/10 bg-white shadow-sm" data-testid="admin-founder-positions-card">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-5 border-b border-tbc-navy/10">
        <div>
          <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
            <Crown className="w-5 h-5 text-tbc-gold" />
            Founding 100 Position Manager
          </h2>
          <p className="text-xs text-tbc-navy/60 mt-1">
            Canonical registry of 100 permanent Founding Business positions. Every assign / move / release is audited.
          </p>
        </div>
        {summary && (
          <div className="flex items-center gap-4 text-xs">
            <StatChip label="Claimed" value={summary.claimed} tone="gold" />
            <StatChip label="Reserved" value={summary.reserved} tone="amber" />
            <StatChip label="Available" value={summary.remaining} tone="emerald" />
            <StatChip
              label="Next available"
              value={summary.next_available ? `#${summary.next_available}` : "—"}
              tone="navy"
            />
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-1.5 px-5 pt-4">
        {[
          ["all", "All 100"],
          ["assigned", "Assigned"],
          ["reserved", "Reserved"],
          ["available", "Available"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            data-testid={`positions-filter-${key}`}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filter === key
                ? "bg-tbc-navy text-white"
                : "bg-tbc-navy/5 text-tbc-navy/70 hover:bg-tbc-navy/10"
            }`}
          >
            {label}
          </button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={load} data-testid="positions-refresh">
          <RotateCcw className="w-3.5 h-3.5 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Grid */}
      <div className="p-5">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-tbc-navy/50">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading positions…
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2" data-testid="positions-grid">
            {rows.map((p) => (
              <PositionTile key={p.position} position={p} onOpen={() => handleOpen(p)} />
            ))}
          </div>
        )}
      </div>

      {/* Action dialog */}
      {selectedPosition && (
        <PositionActionDialog
          open={actionOpen}
          onOpenChange={setActionOpen}
          position={selectedPosition}
          onDone={() => {
            setActionOpen(false);
            setSelectedPosition(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function StatChip({ label, value, tone }) {
  const toneCls =
    tone === "gold"
      ? "text-tbc-gold"
      : tone === "amber"
      ? "text-amber-600"
      : tone === "emerald"
      ? "text-emerald-600"
      : "text-tbc-navy";
  return (
    <div className="text-right">
      <div className={`font-heading text-lg font-semibold ${toneCls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-tbc-navy/50">{label}</div>
    </div>
  );
}

function PositionTile({ position, onOpen }) {
  const style = STATUS_STYLES[position.status] || STATUS_STYLES.AVAILABLE;
  const Icon = style.icon;
  const biz = position.business;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`text-left rounded-xl border ${style.ring} px-3 py-2.5 hover:shadow-md hover:border-tbc-navy/30 transition-all`}
      data-testid={`position-tile-${position.position}`}
    >
      <div className="flex items-center justify-between">
        <span className="font-heading text-tbc-navy text-lg">#{String(position.position).padStart(3, "0")}</span>
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold ${style.label}`}>
          <Icon className="w-3 h-3" />
          {position.status}
        </span>
      </div>
      <div className="mt-1.5 min-h-[24px]">
        {biz ? (
          <div className="text-sm text-tbc-navy font-medium truncate" title={biz.name}>
            {biz.name}
          </div>
        ) : position.status === "RESERVED" ? (
          <div className="text-xs text-tbc-navy/50 italic">Reserved by admin</div>
        ) : (
          <div className="text-xs text-tbc-navy/40">Open — next up when its number comes</div>
        )}
      </div>
      {position.admin_note && (
        <div className="mt-1.5 text-[11px] text-tbc-navy/50 line-clamp-1" title={position.admin_note}>
          📝 {position.admin_note}
        </div>
      )}
      {position.assigned_source && (
        <div className="mt-1 text-[10px] uppercase tracking-widest text-tbc-navy/40">
          Source: {position.assigned_source}
        </div>
      )}
    </button>
  );
}

function PositionActionDialog({ open, onOpenChange, position, onDone }) {
  const [tab, setTab] = useState("assign");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [adminNote, setAdminNote] = useState(position.admin_note || "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Reset when the position changes
    setTab(position.status === "ASSIGNED" ? "release" : "assign");
    setQuery("");
    setResults([]);
    setTarget(null);
    setReason("");
    setAdminNote(position.admin_note || "");
  }, [position]);

  const runSearch = async (q) => {
    setQuery(q);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/founder/admin/business-search`, {
        params: { q },
        withCredentials: true,
      });
      setResults(r.data.results || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const submitAssign = async () => {
    if (!target) {
      toast.error("Pick a business first");
      return;
    }
    if (!reason.trim()) {
      toast.error("Reason is required for audit");
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/founder/positions/admin/${position.position}/assign`,
        {
          business_id: target.business_id,
          reason: reason.trim(),
          replace_existing_holder: position.status === "ASSIGNED",
        },
        { withCredentials: true },
      );
      toast.success(
        position.status === "ASSIGNED"
          ? `Position #${position.position} reassigned to ${target.name}`
          : `${target.name} assigned to position #${position.position}`,
      );
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't assign");
    } finally {
      setSubmitting(false);
    }
  };

  const submitStatus = async (nextStatus) => {
    if (!reason.trim()) {
      toast.error("Reason is required for audit");
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/founder/positions/admin/${position.position}/status`,
        {
          status: nextStatus,
          reason: reason.trim(),
          admin_note: adminNote.trim() || null,
        },
        { withCredentials: true },
      );
      toast.success(
        nextStatus === "AVAILABLE"
          ? `Position #${position.position} is now available`
          : `Position #${position.position} reserved`,
      );
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't change status");
    } finally {
      setSubmitting(false);
    }
  };

  const biz = position.business;
  const isAssigned = position.status === "ASSIGNED";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="text-tbc-navy font-heading flex items-center gap-2">
            <Crown className="w-5 h-5 text-tbc-gold" />
            Founding Business #{String(position.position).padStart(3, "0")}
          </DialogTitle>
          <DialogDescription className="text-tbc-navy/60">
            {isAssigned ? (
              <span>
                Currently held by <strong>{biz?.name || position.business_id}</strong>
                {position.assigned_source ? ` (source: ${position.assigned_source})` : ""}.
              </span>
            ) : position.status === "RESERVED" ? (
              <>Reserved — no business will be auto-assigned to this slot until released.</>
            ) : (
              <>Available — next in line if no lower-numbered slot opens up.</>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1.5 border-b border-tbc-navy/10">
          {[
            ["assign", isAssigned ? "Reassign" : "Assign"],
            ["reserve", "Reserve"],
            ["release", "Release"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`position-action-${key}`}
              className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                tab === key
                  ? "border-tbc-gold text-tbc-navy"
                  : "border-transparent text-tbc-navy/50 hover:text-tbc-navy"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "assign" && (
          <div className="space-y-3">
            {isAssigned && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2 text-xs text-amber-900">
                <ShieldAlert className="w-4 h-4 flex-none mt-0.5" />
                <span>
                  Reassigning this slot will strip {biz?.name || "the current holder"} of their Founder position and audit the action. This cannot be undone from the UI — restore via re-assignment.
                </span>
              </div>
            )}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tbc-navy/40" />
              <input
                value={query}
                onChange={(e) => runSearch(e.target.value)}
                placeholder="Search business by name…"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-tbc-navy/15 text-sm focus:outline-none focus:border-tbc-navy"
                data-testid="position-search-input"
              />
            </div>
            {searching ? (
              <div className="text-xs text-tbc-navy/50 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> searching…
              </div>
            ) : (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-tbc-navy/10 divide-y">
                {results.length === 0 && query.length >= 2 && (
                  <div className="px-3 py-4 text-xs text-tbc-navy/50">No matches</div>
                )}
                {results.map((r) => (
                  <button
                    key={r.business_id}
                    type="button"
                    onClick={() => setTarget(r)}
                    data-testid={`position-search-result-${r.business_id}`}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-tbc-cream/40 flex items-center justify-between ${
                      target?.business_id === r.business_id ? "bg-tbc-cream/60" : ""
                    }`}
                  >
                    <div>
                      <div className="text-tbc-navy font-medium">{r.name}</div>
                      <div className="text-[11px] text-tbc-navy/50 font-mono">{r.business_id}</div>
                    </div>
                    {r.is_founding_member && (
                      <span className="text-[10px] uppercase tracking-widest text-tbc-gold">
                        holds #{r.founding_member_number || "?"}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required for audit) — e.g. Onboarded via legacy grant"
              className="w-full px-3 py-2 rounded-lg border border-tbc-navy/15 text-sm min-h-[70px] focus:outline-none focus:border-tbc-navy"
              data-testid="position-reason-input"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={submitAssign}
                disabled={submitting || !target || !reason.trim()}
                className="bg-tbc-navy text-white hover:bg-tbc-navy/90"
                data-testid="position-submit-assign"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {isAssigned ? "Confirm Reassign" : "Assign"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {tab === "reserve" && (
          <div className="space-y-3">
            <p className="text-sm text-tbc-navy/70">
              Reserving a slot removes any current holder (audited) and prevents auto-assignment until you release it. Use for slots you want to hold for a specific inbound business.
            </p>
            <input
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Admin note (optional, shown on the tile)"
              className="w-full px-3 py-2 rounded-lg border border-tbc-navy/15 text-sm focus:outline-none focus:border-tbc-navy"
              data-testid="position-admin-note"
            />
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required for audit)"
              className="w-full px-3 py-2 rounded-lg border border-tbc-navy/15 text-sm min-h-[70px] focus:outline-none focus:border-tbc-navy"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => submitStatus("RESERVED")}
                disabled={submitting || !reason.trim()}
                className="bg-amber-500 text-white hover:bg-amber-600"
                data-testid="position-submit-reserve"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Reserve slot
              </Button>
            </DialogFooter>
          </div>
        )}

        {tab === "release" && (
          <div className="space-y-3">
            {isAssigned && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 flex items-start gap-2 text-xs text-red-900">
                <X className="w-4 h-4 flex-none mt-0.5" />
                <span>
                  This will remove <strong>{biz?.name || position.business_id}</strong> from their Founder status. Their badge, discounts and permanent record will be cleared. Auditable in the founder_grants log.
                </span>
              </div>
            )}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required for audit)"
              className="w-full px-3 py-2 rounded-lg border border-tbc-navy/15 text-sm min-h-[70px] focus:outline-none focus:border-tbc-navy"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => submitStatus("AVAILABLE")}
                disabled={submitting || !reason.trim()}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="position-submit-release"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Release to AVAILABLE
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
